import {findChildrenByName, setBox3FromObjectArray} from "../../utils/threeUtils";
import {buildingGroup, isFreeWallMode, isOrthogonalMode, mousePosition, tmpGroup} from "../../main";
import {Box3, Euler, Mesh, PlaneGeometry, Raycaster, Vector3} from "three";
import * as Globals from "../../globals";
import {HBEWallDefaultThickness} from "../../globals";
import {nearestAxisCoordinate, parallelCenterline, snapEndToFace, snapPointToGridCorners, snapStartEdge, SNAP_ALIGN_LATERAL, SNAP_AXIS_TOLERANCE, SNAP_BUTT_OVERLAP, SNAP_CLOSE_TOLERANCE, SNAP_JOIN_ALONG, SNAP_MIN_HIT, SNAP_MIN_SEGMENT} from "../../utils/snappingUtils";
import {chooseStartCorner, landsOnMember, pickChainEnd} from "../../utils/chainSnapping";
import {clearWallPreviewState, directionAxisName, directionSign, wallDirection, wallDirectionAxis, wallSnapAxis} from "./wallGeneration";
import {toFixedFloat} from "../../utils/utils";
import {Wall} from "./wall";
import {Text} from "../baseElements/text";
import {TransactionManager} from "../../TransactionManager";
import {setObjectLayer} from "../../utils/layerUtils";
import {
    outerEdgeWallPreviewMaterial,
    previewMaterial,
    previewNotAllowedMaterial
} from "../../constants/materialConstants";
import {degrees90} from "../../constants/mathConstants";
import {raehmMeshName} from "../../constants/nameConstants";
import {addVisualAxis, removeVisualAxis} from "../../utils/visualAxisUtils";
import { getOuterWallPoints } from "../../utils/wallAnalyzer";

const gridSnapTolerance = 0.045;
const wallSnapTolerance = 0.05;
const closeWallSnapTolerance = 0.075;
const wallRaycastTolerance = 0.001;
const wallVectorOffset = 0.5 * Globals.HBEWallDefaultThickness;
const previewYPosition = 4;

let raehmRaycaster = new Raycaster();
raehmRaycaster.params.Mesh.threshold = 0.1;
raehmRaycaster.layers.enable(Globals.layers.raehm);

export class WallPreview {
    constructor(isOuterWall, previousDirection = null) {
        this.allRaehmMeshes = findChildrenByName(buildingGroup, raehmMeshName, false, true);

        this.firstPreviewMesh = null; //Erstes Vorschau Viereck. Wird beim Klick auf Wandbutton erstellt
        this.secondPreviewMesh = null; //Zweite Vorschau der zu erstellenden Wand. Wird nach Platzierung des ersten Punkts/Vorschau generiert
        this.firstWallPlaced = false; //Wird getogglet um festzuhalten ob während der aktuellen Vorschau schon eine Wand platziert wurde
        this.previousDirection = previousDirection; //Richtung der zuletzt gesetzt Wand während aktueller Vorschau sofern vorhanden
        this.newWallPlaced = false;
        this.isOuterWall = isOuterWall;
        this.isOuterEdge = false;
        this.validFirstPosition = false;
        this.chain = null; //First wall of this run {start, axis, sign}. Used to close the loop.

        this.createFirstPreview(); //Erzeugen der ersten Vorschau
    }

    //First preview step
    createFirstPreview() {
        this.firstPreviewMesh = this.createFirstPreviewMesh();
        this.adjustFirstPreview();
        tmpGroup.addWithUuid(this.firstPreviewMesh);
    }

    createFirstPreviewMesh() {
        //Generiere Vorschau für ersten Wandklick
        let geometry = new PlaneGeometry(Globals.HBEWallDefaultThickness, Globals.HBEWallDefaultThickness);
        geometry.rotateX(degrees90); //Rotieren um auf x/z zu liegen
        let previewStartMesh = new Mesh(geometry, previewMaterial);
        previewStartMesh.name = "wallPreviewStart";
        setObjectLayer(previewStartMesh, Globals.layers.layoutViewOnly);

        return previewStartMesh;
    }

    adjustFirstPreview() {
        let snapPoint = this.snapFirstPreview();
        if (snapPoint === null) {
            snapPoint = mousePosition;
        }

        this.firstPreviewMesh.position.copy(snapPoint);
        this.firstPreviewMesh.position.y = previewYPosition;

        this.checkValidFirstPosition();
    }

    snapFirstPreview() {
        let axisPosition = this.snapPointToAxis(mousePosition);
        if (axisPosition !== null) {
            return axisPosition;
        }

        //Snap an Grid probieren, sofern keine Wand gefunden oder die Toleranz zur Wand überschritten wurde.
        let gridPosition = snapPointToGridCorners(mousePosition, 0.1, 0.1); //Position am nächsten Rasterpunkt
        if (gridPosition.distanceTo(mousePosition) <= gridSnapTolerance) {
            return gridPosition;
        }

        return null; //Wenn weder noch zutrifft null zurückgeben. Kein Snappoint konnte berechnet werden. Wird anschließend an Mausposition gesetzt
    }

    snapPointToAxis(point) {
        removeVisualAxis();
        let closestPoint = null;

        if (wallSnapAxis.z.length > 0) {
            closestPoint = this.snapPointToSingleAxis(point, wallSnapAxis.z, "x");
        }
        if (wallSnapAxis.x.length > 0) {
            let tmpClosestPoint = null;
            if (closestPoint !== null) {
                tmpClosestPoint = this.snapPointToSingleAxis(closestPoint, wallSnapAxis.x, "z");
            } else {
                tmpClosestPoint = this.snapPointToSingleAxis(point, wallSnapAxis.x, "z");
            }

            if (tmpClosestPoint !== null) {
                closestPoint = tmpClosestPoint;
            }
        }

        return closestPoint;
    }

    snapPointToSingleAxis(point, lines, direction) {
        if (point === null || point === undefined) {
            return null;
        }

        const nearest = nearestAxisCoordinate(point, lines, direction, SNAP_AXIS_TOLERANCE, HBEWallDefaultThickness + 0.15);
        if (nearest === null) {
            return null;
        }

        const half = HBEWallDefaultThickness * 0.5;
        const closestPoint = new Vector3().copy(point);
        if (direction === "x") {
            closestPoint.x = nearest.coord;
            addVisualAxis(closestPoint, wallDirectionAxis.xAxis);
            closestPoint.x += point.x > nearest.coord ? half : -half;
        } else {
            closestPoint.z = nearest.coord;
            addVisualAxis(closestPoint, wallDirectionAxis.zAxis);
            closestPoint.z += point.z > nearest.coord ? half : -half;
        }
        closestPoint.y = previewYPosition;
        return closestPoint;
    }

    checkValidFirstPosition() {
        let previewBounds = new Box3().setFromObject(this.firstPreviewMesh);
        previewBounds.min.y = 0;
        previewBounds.max.y = 1;

        this.validFirstPosition = true;

        for (let i = 0; i < this.allRaehmMeshes.length; i++) {
            let raehmBounds = new Box3().setFromObject(this.allRaehmMeshes[i]);
            raehmBounds.min.y = 0;
            raehmBounds.max.y = 1;

            raehmBounds.intersect(previewBounds);
            let size = raehmBounds.getSize(new Vector3());
            if (toFixedFloat(size.x) > 0 && toFixedFloat(size.z) > 0) {
                this.validFirstPosition = false;
                break;
            }
        }

        if (this.validFirstPosition) {
            this.firstPreviewMesh.material = previewMaterial;
        } else {
            this.firstPreviewMesh.material = previewNotAllowedMaterial;
        }
    }

    //Functions for second preview step
    createSecondPreview() {
        //Aufruf aus Klick Event.
        //Entfernt die erste preview aus der Ansicht. Wird aber für spätere Berechnungen noch beibehalten.
        this.firstPreviewMesh.removeFromParent();
        this.adjustSecondPreview();
    }

    adjustSecondPreview(overwriteLength = null) {
        /*TODO: Der wenn die Maus in der letzten Wand ist kann es zu überschneidenden Wänden führen.
           Sicherstellen dass die Preview immer außerhalb der Wand startet */

        //Relevante Werte vor Berechnung zurücksetzen
        this.direction = null;
        this.directionAxis = null;
        this.directionVector = null;
        this.firstPositionOverwrite = null;
        this.cancelAfterWallCreation = false;

        //Prüfen ob Richtung begrenzt werden muss: Abhängig von vorher platzierten Wänden, während der gleichen Vorschau
        let allowedDirections = [];
        if (this.previousDirection !== null) {
            //Erlaubte Richtungen festlegen
            //After first wall has been made, if its by x -> limit next wall direction to +-z, if its by z -> limit next wall to +-x
            if (this.previousDirection === wallDirection.negativeX || this.previousDirection === wallDirection.positiveX) {
                allowedDirections = [wallDirection.negativeZ, wallDirection.positiveZ];
            } else if (this.previousDirection === wallDirection.negativeZ || this.previousDirection === wallDirection.positiveZ) {
                allowedDirections = [wallDirection.negativeX, wallDirection.positiveX];
            }
        }

        
        //Orientierung und Länge der Wand/Wandvorschau berechnen. Basierend auf Position der ersten Vorschau und der aktuellen Mausposition. Sowie Richtungsbegrenzungen
        removeVisualAxis();
        this.calculateDirectionVector(allowedDirections);
        //Wenn zweite Wand während einer Vorschau generiert wird:
        this.adjustFirstPreviewPosition();
        if (isOrthogonalMode && this.directionAxis) {
            this.applyDirectionOffset();
        }
        this.alignToParallelShell();
        this.seatStartAgainstWall();

        const rawLength = this.directionVector.length();
        const rawEnd = new Vector3().copy(this.firstPositionOverwrite).add(this.directionVector);
        let closure = null;
        if (overwriteLength === null && rawLength > 0) {
            closure = this.snapEndToClosure(rawEnd);
        }

        let endGuide = null;
        if (closure !== null) {
            this.directionVector = new Vector3().subVectors(closure.point, this.firstPositionOverwrite);
            this.length = this.directionVector.length();
            this.cancelAfterWallCreation = closure.butt;
            endGuide = closure.point;
        } else {
            if (isFreeWallMode) {
                this.length = toFixedFloat(rawLength, 5);
            } else if (overwriteLength !== null) {
                this.length = toFixedFloat(overwriteLength, 5);
            } else {
                let sections = Math.round((toFixedFloat(rawLength, 5)) / Globals.HBESectionWidth);
                if (sections === 0) {
                    sections++;
                }
                this.length = sections * Globals.HBESectionWidth;
            }
            this.directionVector.clampLength(this.length, this.length);
            this.cancelAfterWallCreation = false;
        }

        //Raycast still stops the wall on a real surface. It may extend a grid length that stopped short,
        //but it must not drag a closure snap off the face it already landed on.
        if (this.length > 0) {
            const rayPoint = this.snapToExistingWalls(rawLength, overwriteLength === null);
            if (rayPoint !== null) {
                const rayVector = new Vector3().subVectors(rayPoint, this.firstPositionOverwrite);
                if (closure === null || rayVector.length() < this.length - (HBEWallDefaultThickness + 0.02)) {
                    this.directionVector = rayVector;
                    this.length = rayVector.length();
                    this.cancelAfterWallCreation = false;
                    endGuide = rayPoint;
                }
            }
        }
        if (endGuide !== null && this.directionAxis) {
            addVisualAxis(endGuide, this.directionAxis);
        }
        // Ending on an existing wall or beam finishes the run, like closing the loop does.
        if (!this.cancelAfterWallCreation && this.directionAxis && this.length > 0) {
            const axis = directionAxisName(this.direction);
            const lateral = axis === "x" ? "z" : "x";
            const end = this.firstPositionOverwrite[axis] + this.directionVector[axis];
            this.cancelAfterWallCreation = landsOnMember(end, this.firstPositionOverwrite[lateral], axis, axis === "x" ? wallSnapAxis.z : wallSnapAxis.x);
        }

        //Generierung des eigentlichen Vorschauelements mit berechneten Werten
        this.adjustSecondPreviewMesh();
    }

    calculateDirectionVector(allowedDirections = []) {
        //Richtungsvektor berechnen
        let directionVector = new Vector3().subVectors(mousePosition, this.firstPreviewMesh.position);
        this.firstPositionOverwrite = new Vector3().copy(this.firstPreviewMesh.position); //Position der ersten Vorschau. Punkt wird anschließend abhängig von der Wandrichtung verschoben
        directionVector.setY(0); //Y Koordinate für Berechnung irrelevant > Nullen > Verfälscht sonst die Länge der Wand

        //Zunächst nur orthogonale Wände. Muss ggf. später erweitert werden
        if (isOrthogonalMode) {
            //Ausrichtung entweder nach Richtung mit größerer Distanz oder wenn Richtung vorgegeben per Array dann in diese begrenzen
            let xOriented = Math.abs(directionVector.x) > Math.abs(directionVector.z);
            let directionZAllowed = allowedDirections.includes(wallDirection.positiveZ) || allowedDirections.includes(wallDirection.negativeZ);
            let directionXAllowed = allowedDirections.includes(wallDirection.positiveX) || allowedDirections.includes(wallDirection.negativeX);

            if ((xOriented && !directionZAllowed) || directionXAllowed) {
                if (mousePosition.x > this.firstPreviewMesh.position.x) {
                    this.direction = wallDirection.positiveX;
                } else {
                    this.direction = wallDirection.negativeX;
                }
                this.directionAxis = wallDirectionAxis.xAxis;
            } else {
                if (mousePosition.z > this.firstPreviewMesh.position.z) {
                    this.direction = wallDirection.positiveZ;
                } else {
                    this.direction = wallDirection.negativeZ;
                }
                this.directionAxis = wallDirectionAxis.zAxis;
            }
            this.applyDirectionOffset();
            return;
        }

        let currentLength = directionVector.length(); //Länge des Vektors
        directionVector.clampLength(currentLength + wallVectorOffset, currentLength + wallVectorOffset); //Verlängern um die Distanz zwischen erstem Viereck Mittelpunkt bis Bounds
        this.directionVector = directionVector;
    }

    applyDirectionOffset() {
        const start = this.firstPreviewMesh.position;
        this.firstPositionOverwrite = new Vector3().copy(start);
        const directionVector = new Vector3().subVectors(mousePosition, start);
        directionVector.setY(0);

        if (this.directionAxis === wallDirectionAxis.xAxis) {
            const offsetVector = new Vector3(wallVectorOffset, 0, 0);
            if (this.direction === wallDirection.positiveX) {
                this.firstPositionOverwrite.sub(offsetVector);
            } else {
                this.firstPositionOverwrite.add(offsetVector);
            }
            directionVector.setZ(0);
        } else {
            const offsetVector = new Vector3(0, 0, wallVectorOffset);
            if (this.direction === wallDirection.positiveZ) {
                this.firstPositionOverwrite.sub(offsetVector);
            } else {
                this.firstPositionOverwrite.add(offsetVector);
            }
            directionVector.setX(0);
        }

        const currentLength = directionVector.length();
        directionVector.clampLength(currentLength + wallVectorOffset, currentLength + wallVectorOffset);
        this.directionVector = directionVector;
    }

    // Inner and outer starts sit one wall thickness apart. The cursor picks one, except on the
    // wall that returns to the first corner, which only gets a corner that closes the loop.
    adjustFirstPreviewPosition() {
        if (!this.firstWallPlaced || !this.previousEndPoint || !this.previousDirection) {
            return;
        }
        if (!this.directionAxis) {
            this.firstPreviewMesh.position.copy(this.previousEndPoint);
            return;
        }

        const prev = {end: this.previousEndPoint, axis: directionAxisName(this.previousDirection), sign: directionSign(this.previousDirection)};
        const corner = chooseStartCorner(this.chain, prev, directionAxisName(this.direction), directionSign(this.direction), mousePosition, HBEWallDefaultThickness);
        this.isOuterEdge = corner.style === "outer";
        this.firstPreviewMesh.position.x = corner.center.x;
        this.firstPreviewMesh.position.z = corner.center.z;
        addVisualAxis(this.firstPreviewMesh.position, prev.axis === "x" ? wallDirectionAxis.xAxis : wallDirectionAxis.zAxis);
    }

    seatStartAgainstWall() {
        if (this.isOuterWall || this.firstWallPlaced || !this.directionAxis || !this.firstPositionOverwrite || !this.directionVector) {
            return;
        }

        const axis = this.directionAxis === wallDirectionAxis.xAxis ? "x" : "z";
        const other = axis === "x" ? "z" : "x";
        const forward = Math.sign(this.directionVector[axis]);
        if (forward === 0) {
            return;
        }

        const lines = axis === "x" ? wallSnapAxis.z : wallSnapAxis.x;
        const seated = snapStartEdge(this.firstPositionOverwrite[axis], this.firstPositionOverwrite[other], forward, axis, lines, {
            tolerance: Math.max(SNAP_AXIS_TOLERANCE, Globals.HBESectionWidth),
            preferKind: "wall",
            overlap: SNAP_BUTT_OVERLAP
        });
        if (seated === null) {
            return;
        }

        const shift = seated - this.firstPositionOverwrite[axis];
        if (Math.abs(shift) < 0.0005) {
            return;
        }
        if ((this.directionVector[axis] - shift) * forward < SNAP_MIN_SEGMENT) {
            return;
        }

        this.firstPositionOverwrite[axis] = seated;
        this.directionVector[axis] -= shift;
    }

    alignToParallelShell() {
        if (this.firstWallPlaced || !this.directionAxis || !this.firstPreviewMesh) {
            return;
        }

        const lateral = this.directionAxis === wallDirectionAxis.xAxis ? "z" : "x";
        const lines = lateral === "z" ? wallSnapAxis.x : wallSnapAxis.z;
        const center = parallelCenterline(this.firstPreviewMesh.position[lateral], lines, lateral, SNAP_ALIGN_LATERAL);
        if (center === null) {
            return;
        }

        this.firstPreviewMesh.position[lateral] = center;
        this.applyDirectionOffset();
        const axis = lateral === "z" ? wallDirectionAxis.zAxis : wallDirectionAxis.xAxis;
        addVisualAxis(this.firstPreviewMesh.position, axis);
    }

    // One end snap: closing the loop wins, then the nearest wall face or return-to-start target.
    // A face snap only lines the end up; the run stops only when the loop closes.
    snapEndToClosure(rawEnd) {
        if (!this.directionAxis || !this.firstPositionOverwrite) {
            return null;
        }

        const axis = directionAxisName(this.direction);
        const sign = directionSign(this.direction);
        const lines = axis === "x" ? wallSnapAxis.z : wallSnapAxis.x;
        const tolerance = this.joinSnapTolerance();
        const face = snapEndToFace(this.firstPositionOverwrite, rawEnd, axis, lines, {
            prefer: "choice",
            lateralTolerance: 100,
            tolerance,
            preferKind: "wall",
            overlap: this.isOuterWall ? 0 : SNAP_BUTT_OVERLAP
        });
        const end = pickChainEnd(this.chain, this.firstPositionOverwrite, rawEnd, axis, sign, HBEWallDefaultThickness, {
            tolerance,
            minSegment: SNAP_MIN_SEGMENT,
            faces: face ? [face[axis]] : []
        });
        if (end === null) {
            return null;
        }

        const point = rawEnd.clone();
        point[axis] = end.coord + (end.kind === "butt" && !this.isOuterWall ? sign * SNAP_BUTT_OVERLAP : 0);
        point[axis === "x" ? "z" : "x"] = this.firstPositionOverwrite[axis === "x" ? "z" : "x"];
        return {point, butt: end.kind !== null};
    }

    joinSnapTolerance() {
        return Math.max(SNAP_CLOSE_TOLERANCE, Globals.HBESectionWidth, SNAP_JOIN_ALONG);
    }

    changePreviewMaterial() {
        if (this.isOuterEdge) {
            this.secondPreviewMesh.material = outerEdgeWallPreviewMaterial;
        } else {
            this.secondPreviewMesh.material = previewMaterial;
        }
    }

    snapToExistingWalls(rawLength = this.length, allowExtend = false) {
        //Setzt vorraus das Rähme alle auf einer Höhe liegen && gleiche Höhe
        let yCenter = setBox3FromObjectArray(this.allRaehmMeshes).getCenter(new Vector3()).y; //Mittlere y Werte der Rähme
        let direction = new Vector3().copy(this.directionVector).clampLength(1, 1);  //Normalisierte Richtung der Wand für Raycaster
        let originMin, originMax;
        let firstPreviewBounds = new Box3().setFromObject(this.firstPreviewMesh);

        //Linke und Rechte Bounds der Wand in Laufrichtung
        if (this.directionAxis === wallDirectionAxis.xAxis) {
            originMin = new Vector3(this.firstPreviewMesh.position.x, yCenter, firstPreviewBounds.min.z + wallRaycastTolerance);
            originMax = new Vector3(this.firstPreviewMesh.position.x, yCenter, firstPreviewBounds.max.z - wallRaycastTolerance);
        } else {
            originMin = new Vector3(firstPreviewBounds.min.x + wallRaycastTolerance, yCenter, this.firstPreviewMesh.position.z);
            originMax = new Vector3(firstPreviewBounds.max.x - wallRaycastTolerance, yCenter, this.firstPreviewMesh.position.z);
        }

        const firstHit = (intersections, origin) => {
            for (let i = 0; i < intersections.length; i++) {
                const snapPoint = new Vector3(intersections[i].point.x, origin.y, intersections[i].point.z);
                const distance = origin.distanceTo(snapPoint);
                if (this.acceptsSnapDistance(distance, rawLength, allowExtend)) {
                    return {snapPoint, distance};
                }
            }
            return null;
        };

        // The centerline is the wall we are actually drawing into. A side ray that only
        // clips a pillar or the return of a corner must not stop the wall short of that face.
        const originCenter = new Vector3(this.firstPositionOverwrite.x, yCenter, this.firstPositionOverwrite.z);
        raehmRaycaster.set(originCenter, direction);
        const centerHit = firstHit(raehmRaycaster.intersectObjects(this.allRaehmMeshes, false), originCenter);

        raehmRaycaster.set(originMin, direction);
        const minHit = firstHit(raehmRaycaster.intersectObjects(this.allRaehmMeshes, false), originMin);
        raehmRaycaster.set(originMax, direction);
        const maxHit = firstHit(raehmRaycaster.intersectObjects(this.allRaehmMeshes, false), originMax);

        let snapPoint = null;
        if (centerHit !== null) {
            snapPoint = centerHit.snapPoint;
        } else if (maxHit !== null && minHit !== null) {
            snapPoint = minHit.distance < maxHit.distance ? minHit.snapPoint : maxHit.snapPoint;
        } else if (maxHit !== null) {
            snapPoint = maxHit.snapPoint;
        } else if (minHit !== null) {
            snapPoint = minHit.snapPoint;
        }

        if (snapPoint !== null) {
            snapPoint.y = previewYPosition;
            if (this.directionAxis === wallDirectionAxis.xAxis) {
                snapPoint.z = this.firstPositionOverwrite.z;
            } else {
                snapPoint.x = this.firstPositionOverwrite.x;
            }
            return snapPoint;
        }

        return null;
    }

    acceptsSnapDistance(distance, rawLength, allowExtend) {
        if (distance < SNAP_MIN_HIT) {
            return false;
        }
        if (distance <= this.length + 0.001) {
            return true;
        }
        return allowExtend && Math.abs(distance - rawLength) <= SNAP_CLOSE_TOLERANCE;
    }

    adjustSecondPreviewMesh() {
        //Position des zweiten Vorschau Meshes
        let position = new Vector3().copy(this.firstPositionOverwrite).add(new Vector3().copy(this.directionVector).multiplyScalar(0.5));
        position.y = previewYPosition;

        //Neuen Mesh generieren und der Szene hinzufügen, falls erste mal aufgerufen. Sonst nur Geometrie aktualisieren > Performancegründe
        if (this.secondPreviewMesh === null) {
            this.secondPreviewMesh = this.createSecondPreviewMesh();
            this.measurementText = this.createMeasurementText();
            setObjectLayer(this.measurementText, Globals.layers.layoutViewOnly);
            tmpGroup.addWithUuid(this.secondPreviewMesh);
            tmpGroup.addWithUuid(this.measurementText);
        } else {
            if (this.newWallPlaced) {
                tmpGroup.addWithUuid(this.secondPreviewMesh);
                tmpGroup.addWithUuid(this.measurementText);
                this.newWallPlaced = false;
            }

            let geometry = new PlaneGeometry(this.length, Globals.HBEWallDefaultThickness);
            geometry.rotateX(degrees90); //Rotieren um auf x/z zu liegen
            this.secondPreviewMesh.geometry.dispose();
            this.secondPreviewMesh.geometry = geometry;

            this.measurementText = this.createMeasurementText(this.measurementText);
        }

        //Rotation der Vorschau basierend auf Wandrichtung einstellen
        if (this.direction === wallDirection.positiveZ) {
            this.secondPreviewMesh.rotation.y = -Math.PI / 2;
        } else if (this.direction === wallDirection.negativeZ) {
            this.secondPreviewMesh.rotation.y = Math.PI / 2;
        } else {
            this.secondPreviewMesh.rotation.y = 0;
        }

        //Position übernehmen
        this.secondPreviewMesh.position.copy(position);

        this.changePreviewMaterial();
    }

    createSecondPreviewMesh() {
        //Generiere Vorschau für ersten Wandklick
        let geometry = new PlaneGeometry(this.length, Globals.HBEWallDefaultThickness);
        geometry.rotateX(Math.PI / 2); //Rotieren um auf x/z zu liegen
        let previewMesh = new Mesh(geometry, previewMaterial);
        previewMesh.name = "wallPreview";
        setObjectLayer(previewMesh, Globals.layers.layoutViewOnly);
        
        return previewMesh;
    }

    //Functions for creating the wall
    createWallFromPreview(placeWall = true, usePreviewLength = false) {
        //Erstellung der eigentlich Wand basierend auf der Vorschau
        if (!this.firstWallPlaced && this.directionAxis) {
            // Taken from the seated start face, not the start marker, so closing targets match the real wall.
            const axis = directionAxisName(this.direction);
            const sign = directionSign(this.direction);
            const start = {x: this.firstPositionOverwrite.x, z: this.firstPositionOverwrite.z};
            start[axis] += sign * HBEWallDefaultThickness * 0.5;
            this.chain = {start, axis, sign};
        }

        TransactionManager.startTransaction();

        this.secondPreviewMesh.removeFromParent();
        this.measurementText.removeFromParent();
        let length = usePreviewLength ? this.length : toFixedFloat(Math.max(Math.abs(this.directionVector.x), Math.abs(this.directionVector.z)),5);
        let wall = new Wall(this.secondPreviewMesh, this.direction, length, this.isOuterWall, placeWall); //Wand erstellen

        //TODO: Wand Richtung / Start&Endpunkt mit in userData aufnehmen?
        if (placeWall) {
            buildingGroup.addWithUuid(wall); //Hinzufügen
        }

        TransactionManager.stopTransaction();

        //Abbruchbedingung wenn Wand in einem Snappoint an einer Wand endet
        if (this.cancelAfterWallCreation) {
            clearWallPreviewState();
            return false;
        } else {

            if(this.isOuterWall && getOuterWallPoints(false,false).length >= 4)
            {
                clearWallPreviewState();
                return false;
            }
        }

        this.allRaehmMeshes = findChildrenByName(buildingGroup, raehmMeshName, false, true); //Updaten der Liste aller Rähme in der Szene

        this.firstWallPlaced = true;
        this.newWallPlaced = true;

        //Position der ersten Vorschau updaten an das Ende der angelegten Wand. Für Berechnung der nächsten Wandvorschau
        //ST: Commenting out the following does not change anything, do we need them?
        //let tmpLength = this.directionVector.length();
        //let tmpDirectionVector = new Vector3().copy(this.directionVector).clampLength(tmpLength - wallVectorOffset, tmpLength - wallVectorOffset);
        //let newPreviewPosition = new Vector3().copy(this.firstPositionOverwrite).add(tmpDirectionVector);
        //this.firstPreviewMesh.position.copy(newPreviewPosition);
        this.previousDirection = this.direction;
        this.previousEndPoint = new Vector3().copy(this.firstPositionOverwrite).add(this.directionVector);

        return true;
    }

    //Utils
    createMeasurementText(measurementText = null) {
        let size = new Box3().setFromObject(this.secondPreviewMesh).getSize(new Vector3());
        let text = Math.abs(this.directionVector.x) > Math.abs(this.directionVector.z) ? toFixedFloat(size.x) : toFixedFloat(size.z);
        text = text.toFixed(Globals.roundToDecimals);

        if (measurementText === null) {
            measurementText = new Text(text, 0.2, 'center', -0.25, new Vector3(0, 0, 0), new Euler(-Math.PI / 2, 0, 0));
            measurementText.color = 0x000000;
        } else {
            measurementText.text = text;
        }

        measurementText.position.x = this.secondPreviewMesh.position.x;
        measurementText.position.y = previewYPosition;
        measurementText.position.z = this.secondPreviewMesh.position.z;
        measurementText.rotation.z = this.secondPreviewMesh.rotation.y === 0 ? 0 : Math.PI / 2;

        measurementText.sync();
        return measurementText;
    }
}
